import React, { useState } from "react";
import { Plus, Trash2, RotateCcw } from "lucide-react";
import { toast } from "@/lib/toast";
import { LIST_META, TONE_OPTIONS, slugify } from "../lib/custom-lists";
import { useConfirm } from "./ConfirmDialog";

/**
 * Inline CRUD editor for a custom list (event types, family statuses, etc.).
 * Props:
 *  - listKey:   key in the custom-lists store (e.g. "eventTypes")
 *  - items:     current array of { value, label, tone? }
 *  - onChange:  (newItems) => void
 *  - onReset:   () => void
 */
export default function ListEditor({ listKey, items, onChange, onReset }) {
  const meta = LIST_META[listKey] || { label: listKey, supportsTone: false };
  const [newLabel, setNewLabel] = useState("");
  const [newTone, setNewTone] = useState("slate");
  const [confirm, ConfirmUI] = useConfirm();

  const addItem = () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Escribe un nombre");
      return;
    }
    // Generate a unique value based on the label.
    let base = slugify(label);
    let value = base;
    let n = 2;
    while (items.some((it) => it.value === value)) {
      value = `${base}_${n++}`;
    }
    const entry = { value, label };
    if (meta.supportsTone) entry.tone = newTone;
    onChange([...items, entry]);
    setNewLabel("");
    setNewTone("slate");
  };

  const updateLabel = (idx, label) => {
    const next = items.slice();
    next[idx] = { ...next[idx], label };
    onChange(next);
  };

  const updateTone = (idx, tone) => {
    const next = items.slice();
    next[idx] = { ...next[idx], tone };
    onChange(next);
  };

  const removeItem = async (idx) => {
    const it = items[idx];
    const ok = await confirm({
      title: `¿Eliminar "${it.label}"?`,
      description:
        "Si algún registro ya usa este valor, seguirá guardado pero aparecerá sin etiqueta.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: `¿Restaurar lista "${meta.label}"?`,
      description: "Se volverá a los valores por defecto.",
      confirmLabel: "Restaurar",
    });
    if (!ok) return;
    onReset();
    toast.success("Lista restaurada");
  };

  return (
    <section
      className="bg-white dark:bg-slate-800 rounded-[20px] p-4 border border-slate-100 dark:border-slate-700/60 space-y-3"
      data-testid={`list-editor-${listKey}`}
    >
      {ConfirmUI}
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {meta.label}
        </h3>
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          data-testid={`list-editor-reset-${listKey}`}
        >
          <RotateCcw size={11} /> Restaurar
        </button>
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && (
          <p className="text-xs text-slate-400 dark:text-slate-500">Lista vacía. Agrega una opción.</p>
        )}
        {items.map((it, idx) => (
          <div key={it.value} className="flex items-center gap-2">
            <input
              type="text"
              value={it.label}
              onChange={(e) => updateLabel(idx, e.target.value)}
              className="flex-1 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-[12px] px-3 py-2 text-[16px] text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-brand/10 dark:focus:ring-blue-500/10 focus:border-brand/50 dark:focus:border-blue-500/50 outline-none"
              data-testid={`list-editor-item-label-${listKey}-${it.value}`}
            />
            {meta.supportsTone && (
              <select
                value={it.tone || "slate"}
                onChange={(e) => updateTone(idx, e.target.value)}
                className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-[12px] px-2 py-2 text-xs text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-brand/10 dark:focus:ring-blue-500/10 focus:border-brand/50 outline-none"
                data-testid={`list-editor-item-tone-${listKey}-${it.value}`}
              >
                {TONE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => removeItem(idx)}
              className="text-red-400 hover:text-red-600 p-2"
              data-testid={`list-editor-item-delete-${listKey}-${it.value}`}
              aria-label="Eliminar"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-slate-100 dark:border-slate-700/60">
        <input
          type="text"
          placeholder="Nuevo…"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          className="flex-1 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-[12px] px-3 py-2 text-[16px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-brand/10 dark:focus:ring-blue-500/10 focus:border-brand/50 dark:focus:border-blue-500/50 outline-none"
          data-testid={`list-editor-new-${listKey}`}
        />
        {meta.supportsTone && (
          <select
            value={newTone}
            onChange={(e) => setNewTone(e.target.value)}
            className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-[12px] px-2 py-2 text-xs text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-brand/10 dark:focus:ring-blue-500/10 focus:border-brand/50 outline-none"
            data-testid={`list-editor-new-tone-${listKey}`}
          >
            {TONE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={addItem}
          className="h-11 w-11 shrink-0 rounded-[14px] bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700"
          data-testid={`list-editor-add-${listKey}`}
          aria-label="Agregar"
        >
          <Plus size={16} />
        </button>
      </div>
    </section>
  );
}
